import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Globe, Plus, Edit, Trash2, Eye, Settings, Save, X, Layout, Type, Image as ImageIcon, ArrowUp, ArrowDown, Upload, RefreshCw, Palette, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getSectionDefaults } from '../utils/sectionDefaults';
import { FontSelector, loadGoogleFont } from '../components/FontSelector';
import { ColorPicker } from '../components/ColorPicker';
import { SectionEditModal } from '../components/SectionEditModal';
import { BUSINESS_TEMPLATES } from '../utils/businessTemplates';
import { UpgradeModal } from '../components/UpgradeModal';
import { useSubscription } from '../hooks/useSubscription';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Use Business Templates
const ENHANCED_TEMPLATES = BUSINESS_TEMPLATES;

// Keep old templates for backwards compatibility
const LEGACY_TEMPLATES = [
  {
    id: 'restaurant-full',
    name: 'Restaurant Pro',
    description: 'Complete restaurant website with gallery and testimonials',
    preview: '🍽️',
    color: '#f59e0b',
    sections: [
      { id: 'h1', type: 'hero', content: { ...getSectionDefaults('hero'), heading: 'Welcome to Our Restaurant', subheading: 'Experience authentic flavors', backgroundImage: 'https://images.pexels.com/photos/1199957/pexels-photo-1199957.jpeg?auto=compress&cs=tinysrgb&w=1920' }},
      { id: 'a1', type: 'about', content: { ...getSectionDefaults('about'), heading: 'Our Story', text: 'For over 20 years, we\'ve been serving delicious cuisine.' }},
      { id: 's1', type: 'services', content: { ...getSectionDefaults('services'), heading: 'Our Menu', items: [{ icon: '🍕', title: 'Fresh Pizza', description: 'Wood-fired authentic pizzas' }, { icon: '🍝', title: 'Pasta', description: 'Homemade pasta daily' }, { icon: '🥗', title: 'Fresh Salads', description: 'Garden-fresh ingredients' }]}},
      { id: 'g1', type: 'gallery', content: { ...getSectionDefaults('gallery'), heading: 'Our Dishes' }},
      { id: 't1', type: 'testimonials', content: { ...getSectionDefaults('testimonials'), heading: 'Happy Customers' }},
      { id: 'c1', type: 'contact', content: { ...getSectionDefaults('contact'), heading: 'Visit Us Today' }}
    ]
  },
  {
    id: 'portfolio-full',
    name: 'Creative Portfolio Pro',
    description: 'Complete portfolio with features, stats, and team',
    preview: '🎨',
    color: '#8b5cf6',
    sections: [
      { id: 'h2', type: 'hero', content: { ...getSectionDefaults('hero'), heading: 'Creative Design Studio', subheading: 'Bringing your vision to life', backgroundImage: 'https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg?auto=compress&cs=tinysrgb&w=1920' }},
      { id: 'f1', type: 'features', content: { ...getSectionDefaults('features'), heading: 'What We Offer' }},
      { id: 'st1', type: 'stats', content: { ...getSectionDefaults('stats'), heading: 'Our Impact' }},
      { id: 'g2', type: 'gallery', content: { ...getSectionDefaults('gallery'), heading: 'Our Work' }},
      { id: 'tm1', type: 'team', content: { ...getSectionDefaults('team'), heading: 'Meet the Team' }},
      { id: 'pr1', type: 'pricing', content: { ...getSectionDefaults('pricing'), heading: 'Our Packages' }},
      { id: 'c2', type: 'contact', content: { ...getSectionDefaults('contact'), heading: 'Get In Touch' }}
    ]
  },
  {
    id: 'business-full',
    name: 'Business Complete',
    description: 'Full business website with all sections',
    preview: '💼',
    color: '#3b82f6',
    sections: [
      { id: 'h3', type: 'hero', content: { ...getSectionDefaults('hero'), heading: 'Grow Your Business', subheading: 'Solutions that drive results' }},
      { id: 'f2', type: 'features', content: { ...getSectionDefaults('features'), heading: 'Why Choose Us' }},
      { id: 'a2', type: 'about', content: { ...getSectionDefaults('about'), heading: 'About Our Company' }},
      { id: 'st2', type: 'stats', content: { ...getSectionDefaults('stats'), heading: 'By The Numbers' }},
      { id: 'pr2', type: 'pricing', content: { ...getSectionDefaults('pricing'), heading: 'Pricing Plans' }},
      { id: 't2', type: 'testimonials', content: { ...getSectionDefaults('testimonials'), heading: 'Client Reviews' }},
      { id: 'faq1', type: 'faq', content: { ...getSectionDefaults('faq'), heading: 'Common Questions' }},
      { id: 'n1', type: 'newsletter', content: { ...getSectionDefaults('newsletter'), heading: 'Stay Updated' }},
      { id: 'c3', type: 'contact', content: { ...getSectionDefaults('contact'), heading: 'Contact Us' }}
    ]
  }
];

// Draggable Section Component
function SortableSection({ id, section, index, totalSections, onEdit, onDelete, onMoveUp, onMoveDown }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-4">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-2 hover:bg-gray-100 rounded">
              <GripVertical className="w-5 h-5 text-gray-400" />
            </div>
            <span className="text-sm font-semibold text-gray-700 uppercase">
              {section.type} Section
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onEdit(section.id)} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
              Edit
            </button>
            <button onClick={() => onDelete(section.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        <SectionPreview section={section} />
      </div>
    </div>
  );
}

// Section Preview Component
function SectionPreview({ section }) {
  const renderPreview = () => {
    switch (section.type) {
      case 'hero':
        return (
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-8 rounded text-center">
            <h2 className="text-3xl font-bold mb-2">{section.content.heading}</h2>
            <p className="text-lg mb-4">{section.content.subheading}</p>
            <button className="px-4 py-2 bg-white text-blue-600 rounded font-semibold">{section.content.buttonText}</button>
          </div>
        );
      case 'about':
        return (
          <div className="bg-gray-50 p-6 rounded">
            <h3 className="text-2xl font-bold mb-3">{section.content.heading}</h3>
            <p className="text-gray-600">{section.content.text?.substring(0, 150)}...</p>
          </div>
        );
      case 'services':
        return (
          <div className="bg-white p-6 rounded">
            <h3 className="text-2xl font-bold mb-4 text-center">{section.content.heading}</h3>
            <div className="grid grid-cols-3 gap-4">
              {section.content.items?.map((item, i) => (
                <div key={i} className="text-center p-3 border rounded">
                  <div className="text-3xl mb-2">{item.icon}</div>
                  <h4 className="font-semibold text-sm">{item.title}</h4>
                </div>
              ))}
            </div>
          </div>
        );
      case 'contact':
        return (
          <div className="bg-gray-900 text-white p-6 rounded text-center">
            <h3 className="text-2xl font-bold mb-4">{section.content.heading}</h3>
            <div className="text-sm space-y-1">
              <p>📧 {section.content.email}</p>
              <p>📞 {section.content.phone}</p>
            </div>
          </div>
        );
      case 'gallery':
        return (
          <div className="bg-white p-6 rounded">
            <h3 className="text-2xl font-bold mb-4 text-center">{section.content.heading}</h3>
            <div className="grid grid-cols-3 gap-2">
              {section.content.images?.slice(0, 6).map((img, i) => (
                <div key={i} className="aspect-square bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        );
      case 'testimonials':
        return (
          <div className="bg-gray-50 p-6 rounded">
            <h3 className="text-2xl font-bold mb-4 text-center">{section.content.heading}</h3>
            <div className="grid grid-cols-2 gap-4">
              {section.content.testimonials?.slice(0, 2).map((t, i) => (
                <div key={i} className="bg-white p-3 rounded shadow-sm text-sm">
                  <p className="text-gray-600 mb-2">"{t.text.substring(0, 80)}..."</p>
                  <p className="font-semibold text-xs">{t.author}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'features':
        return (
          <div className="bg-white p-6 rounded">
            <h3 className="text-2xl font-bold mb-4 text-center">{section.content.heading}</h3>
            <div className="grid grid-cols-3 gap-3">
              {section.content.features?.slice(0, 6).map((f, i) => (
                <div key={i} className="text-center p-2 border rounded">
                  <div className="text-2xl mb-1">{f.icon}</div>
                  <h4 className="font-semibold text-xs">{f.title}</h4>
                </div>
              ))}
            </div>
          </div>
        );
      case 'pricing':
        return (
          <div className="bg-gray-50 p-6 rounded">
            <h3 className="text-2xl font-bold mb-4 text-center">{section.content.heading}</h3>
            <div className="grid grid-cols-3 gap-3">
              {section.content.plans?.map((plan, i) => (
                <div key={i} className="bg-white border rounded p-3 text-center">
                  <h4 className="font-bold text-sm">{plan.name}</h4>
                  <p className="text-2xl font-bold my-2">${plan.price}</p>
                  <p className="text-xs text-gray-500">{plan.period}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'team':
        return (
          <div className="bg-white p-6 rounded">
            <h3 className="text-2xl font-bold mb-4 text-center">{section.content.heading}</h3>
            <div className="grid grid-cols-4 gap-3">
              {section.content.members?.map((member, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-2"></div>
                  <h4 className="font-semibold text-xs">{member.name}</h4>
                  <p className="text-xs text-gray-500">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'faq':
        return (
          <div className="bg-white p-6 rounded">
            <h3 className="text-2xl font-bold mb-4">{section.content.heading}</h3>
            <div className="space-y-2">
              {section.content.faqs?.slice(0, 3).map((faq, i) => (
                <div key={i} className="border rounded p-2">
                  <h4 className="font-semibold text-sm">{faq.question}</h4>
                  <p className="text-xs text-gray-600 mt-1">{faq.answer.substring(0, 60)}...</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'newsletter':
        return (
          <div className="bg-blue-600 text-white p-6 rounded text-center">
            <h3 className="text-2xl font-bold mb-2">{section.content.heading}</h3>
            <p className="mb-4 text-sm">{section.content.subheading}</p>
            <div className="flex gap-2 max-w-md mx-auto">
              <input type="email" placeholder="Email" className="flex-1 px-3 py-2 rounded text-black" disabled />
              <button className="px-4 py-2 bg-white text-blue-600 rounded font-semibold text-sm">Subscribe</button>
            </div>
          </div>
        );
      case 'video':
        return (
          <div className="bg-gray-900 p-6 rounded text-center">
            <h3 className="text-2xl font-bold mb-4 text-white">{section.content.heading}</h3>
            <div className="aspect-video bg-gray-800 rounded flex items-center justify-center">
              <span className="text-white text-4xl">▶</span>
            </div>
          </div>
        );
      case 'stats':
        return (
          <div className="bg-gray-900 text-white p-6 rounded">
            <h3 className="text-2xl font-bold mb-4 text-center">{section.content.heading}</h3>
            <div className="grid grid-cols-4 gap-4">
              {section.content.stats?.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-bold text-blue-400">{stat.number}</div>
                  <p className="text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return <div className="text-gray-500 text-center p-4">Preview not available</div>;
    }
  };

  return <div className="text-sm">{renderPreview()}</div>;
}

export default function WebsiteBuilderEnhanced() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSite, setCurrentSite] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  
  // Subscription management
  const { canUse, limit, showUpgradeModal, setShowUpgradeModal, checkAndIncrement } = useSubscription('websites');
  
  // New site form
  const [newSite, setNewSite] = useState({ siteName: '', subdomain: '', template: null });
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Stock Images
  const [showStockImages, setShowStockImages] = useState(false);
  const [stockImages, setStockImages] = useState([]);
  const [stockImageQuery, setStockImageQuery] = useState('');
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageSelectionTarget, setImageSelectionTarget] = useState(null);
  
  // Image Upload
  const [uploadingImage, setUploadingImage] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API}/website-builder/sites`);
      setSites(response.data.sites || []);
    } catch (error) {
      console.error('Failed to load sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSite = async (useTemplate = false) => {
    if (!newSite.siteName || !newSite.subdomain) {
      toast.error('Please fill in all fields');
      return;
    }

    // Check subscription limit
    const allowed = await checkAndIncrement();
    if (!allowed) {
      return; // Modal will show automatically
    }

    try {
      const response = await axios.post(`${API}/website-builder/sites`, {
        siteName: newSite.siteName,
        subdomain: newSite.subdomain,
        userId: 'anonymous',
        settings: {
          primaryColor: '#3b82f6',
          secondaryColor: '#1e40af',
          fontFamily: 'Inter',
          textColor: '#1f2937'
        }
      });
      
      const siteId = response.data.site.id;
      const homePageId = response.data.homePage.id;

      if (useTemplate && newSite.template) {
        const template = ENHANCED_TEMPLATES.find(t => t.id === newSite.template);
        if (template) {
          await axios.put(`${API}/website-builder/pages/${homePageId}`, {
            sections: template.sections
          });
        }
      }

      toast.success('Website created!');
      setNewSite({ siteName: '', subdomain: '', template: null });
      setShowTemplates(false);
      loadSites();
      openEditor(siteId);
    } catch (error) {
      console.error('Failed to create site:', error);
      toast.error(error.response?.data?.detail || 'Failed to create site');
    }
  };

  const deleteSite = async (siteId) => {
    if (!window.confirm('Delete this website? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/website-builder/sites/${siteId}`);
      toast.success('Website deleted');
      loadSites();
    } catch (error) {
      toast.error('Failed to delete website');
    }
  };

  const openEditor = async (siteId) => {
    try {
      const [siteResponse, pagesResponse] = await Promise.all([
        axios.get(`${API}/website-builder/sites/${siteId}`),
        axios.get(`${API}/website-builder/sites/${siteId}/pages`)
      ]);
      
      setCurrentSite(siteResponse.data.site);
      const homePage = pagesResponse.data.pages.find(p => p.isHome) || pagesResponse.data.pages[0];
      setCurrentPage(homePage);
    } catch (error) {
      toast.error('Failed to open editor');
    }
  };

  const savePage = async () => {
    try {
      await axios.put(`${API}/website-builder/pages/${currentPage.id}`, {
        sections: currentPage.sections
      });
      toast.success('Changes saved!');
    } catch (error) {
      toast.error('Failed to save changes');
    }
  };

  const publishSite = async () => {
    try {
      await axios.post(`${API}/website-builder/sites/${currentSite.id}/publish`);
      toast.success(`Site published! View at: ${window.location.origin}/sites/${currentSite.subdomain}`);
      setCurrentSite({ ...currentSite, isPublished: true });
    } catch (error) {
      toast.error('Failed to publish site');
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: `${type}-${Date.now()}`,
      type,
      content: getSectionDefaults(type),
      styles: {}
    };
    
    setCurrentPage({
      ...currentPage,
      sections: [...currentPage.sections, newSection]
    });
    toast.success('Section added!');
  };

  const updateSectionContent = (sectionId, field, value) => {
    setCurrentPage({
      ...currentPage,
      sections: currentPage.sections.map(s =>
        s.id === sectionId
          ? { ...s, content: { ...s.content, [field]: value } }
          : s
      )
    });
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    setCurrentPage({
      ...currentPage,
      sections: currentPage.sections.filter(s => s.id !== sectionId)
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setCurrentPage((current) => {
        const oldIndex = current.sections.findIndex(s => s.id === active.id);
        const newIndex = current.sections.findIndex(s => s.id === over.id);
        
        return {
          ...current,
          sections: arrayMove(current.sections, oldIndex, newIndex)
        };
      });
      toast.success('Section moved!');
    }
  };

  // Stock image functions
  const searchStockImages = async (query = stockImageQuery) => {
    if (!query.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoadingImages(true);
    try {
      const response = await axios.get(`${API}/images/search`, {
        params: { query, source: 'combined', per_page: 20 }
      });
      
      if (response.data.success) {
        setStockImages(response.data.images || []);
      }
    } catch (error) {
      toast.error('Failed to load stock images');
    } finally {
      setLoadingImages(false);
    }
  };

  const loadCuratedImages = async () => {
    setLoadingImages(true);
    try {
      const response = await axios.get(`${API}/images/curated`, {
        params: { per_page: 20 }
      });
      
      if (response.data.success) {
        setStockImages(response.data.images || []);
      }
    } catch (error) {
      toast.error('Failed to load curated images');
    } finally {
      setLoadingImages(false);
    }
  };

  const openImagePicker = (sectionId, field) => {
    setImageSelectionTarget({ sectionId, field });
    setShowStockImages(true);
    loadCuratedImages();
  };

  const selectImage = (imageUrl) => {
    if (imageSelectionTarget) {
      const { sectionId, field } = imageSelectionTarget;
      
      // Handle nested fields like "images.0.url"
      if (field.includes('.')) {
        const [arrayField, index, nestedField] = field.split('.');
        const section = currentPage.sections.find(s => s.id === sectionId);
        const items = [...section.content[arrayField]];
        items[parseInt(index)] = { ...items[parseInt(index)], [nestedField]: imageUrl };
        updateSectionContent(sectionId, arrayField, items);
      } else {
        updateSectionContent(sectionId, field, imageUrl);
      }
      
      setShowStockImages(false);
      setImageSelectionTarget(null);
      toast.success('Image selected!');
    }
  };

  const uploadImage = async (file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/images/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        if (imageSelectionTarget) {
          const { sectionId, field } = imageSelectionTarget;
          
          // Handle nested fields like "images.0.url"
          if (field.includes('.')) {
            const [arrayField, index, nestedField] = field.split('.');
            const section = currentPage.sections.find(s => s.id === sectionId);
            const items = [...section.content[arrayField]];
            items[parseInt(index)] = { ...items[parseInt(index)], [nestedField]: fullUrl };
            updateSectionContent(sectionId, arrayField, items);
          } else {
            updateSectionContent(sectionId, field, fullUrl);
          }
          
          setShowStockImages(false);
          setImageSelectionTarget(null);
        }
        toast.success('Image uploaded!');
        return fullUrl;
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Sites Management View
  if (!currentSite) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Website Builder</h1>
              <p className="text-gray-600 mt-1">Create and manage your websites</p>
              {limit && !limit.allowed && (
                <p className="text-sm text-orange-600 mt-2 font-semibold">
                  ⚠️ You've reached your limit ({limit.current}/{limit.max} websites). 
                  <button onClick={() => navigate('/pricing')} className="underline ml-1">Upgrade to create more</button>
                </p>
              )}
              {limit && limit.allowed && limit.max !== 'unlimited' && (
                <p className="text-sm text-gray-500 mt-2">
                  {limit.remaining} of {limit.max} websites remaining
                </p>
              )}
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              ← Back to Dashboard
            </Button>
          </div>

          {/* Create New Site Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create New Website</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Site Name (e.g., My Restaurant)"
                    value={newSite.siteName}
                    onChange={(e) => setNewSite({ ...newSite, siteName: e.target.value })}
                  />
                  <Input
                    placeholder="Subdomain (e.g., myrestaurant)"
                    value={newSite.subdomain}
                    onChange={(e) => setNewSite({ ...newSite, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Button onClick={() => setShowTemplates(!showTemplates)} variant="outline" className="flex-1">
                    {showTemplates ? '✕ Hide Templates' : '🎨 Choose from Templates'}
                  </Button>
                </div>

                {showTemplates && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                    {ENHANCED_TEMPLATES.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => setNewSite({ ...newSite, template: template.id })}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          newSite.template === template.id
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-4xl">{template.preview}</div>
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900">{template.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {template.sections.length} sections included
                            </p>
                            {newSite.template === template.id && (
                              <p className="text-xs text-blue-600 mt-2 font-semibold">✓ Selected</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-3">
                  {showTemplates && newSite.template ? (
                    <Button onClick={() => createSite(true)} className="flex-1 bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Create with {ENHANCED_TEMPLATES.find(t => t.id === newSite.template)?.name}
                    </Button>
                  ) : (
                    <Button onClick={() => createSite(false)} className="flex-1 bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Blank Website
                    </Button>
                  )}
                </div>

                <p className="text-sm text-gray-500">
                  Your site will be available at: <strong>{newSite.subdomain || 'yoursite'}.digidream.com</strong>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Existing Sites */}
          <div>
            <h2 className="text-xl font-bold mb-4">Your Websites</h2>
            {sites.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Globe className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No websites yet. Create your first one above!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sites.map((site) => (
                  <Card key={site.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-bold text-lg">{site.siteName}</h3>
                          <p className="text-sm text-gray-500">{site.subdomain}.digidream.com</p>
                        </div>
                        {site.isPublished && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Published
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => openEditor(site.id)} className="flex-1" size="sm">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        {site.isPublished && (
                          <Button
                            onClick={() => window.open(`/sites/${site.subdomain}`, '_blank')}
                            variant="outline"
                            size="sm"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          onClick={() => deleteSite(site.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Editor View - Part 1 of render continues below due to size...
  // This is a simplified version. Full implementation requires more code for edit modals, etc.
  
  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Top Bar */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => setCurrentSite(null)} variant="ghost" size="sm">
            ← Back
          </Button>
          <div>
            <h2 className="font-bold">{currentSite.siteName}</h2>
            <p className="text-sm text-gray-500">{currentSite.subdomain}.digidream.com</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={savePage} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
          <Button onClick={publishSite} className="bg-blue-600 hover:bg-blue-700">
            <Globe className="w-4 h-4 mr-2" />
            Publish Site
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Section Library */}
        <div className="w-64 bg-white border-r overflow-y-auto p-4">
          <h3 className="font-bold mb-4">Add Sections</h3>
          <div className="space-y-2">
            {['hero', 'about', 'services', 'features', 'gallery', 'testimonials', 'pricing', 'team', 'faq', 'newsletter', 'video', 'stats', 'contact'].map(type => (
              <Button
                key={type}
                onClick={() => addSection(type)}
                variant="outline"
                className="w-full justify-start"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg font-bold mb-4">Page Sections (Drag to Reorder)</h3>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={currentPage.sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                {currentPage.sections.map((section, index) => (
                  <SortableSection
                    key={section.id}
                    id={section.id}
                    section={section}
                    index={index}
                    totalSections={currentPage.sections.length}
                    onEdit={() => setEditingSection(section)}
                    onDelete={deleteSection}
                  />
                ))}
              </SortableContext>
            </DndContext>
            
            {currentPage.sections.length === 0 && (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Layout className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No sections yet. Add sections from the left panel.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal - Comprehensive for all section types */}
      <SectionEditModal
        section={editingSection}
        onClose={() => setEditingSection(null)}
        onUpdate={updateSectionContent}
        onOpenImagePicker={openImagePicker}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="websites"
        currentLimit={limit}
      />

      {/* Stock Image Browser Modal */}
      {showStockImages && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">📸 Stock Images & Upload</h2>
                <Button onClick={() => setShowStockImages(false)} variant="ghost" size="sm">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="flex gap-3">
                <Input
                  placeholder="Search images (e.g., restaurant, business, nature)..."
                  value={stockImageQuery}
                  onChange={(e) => setStockImageQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchStockImages()}
                  className="flex-1"
                />
                <Button onClick={() => searchStockImages()} className="bg-blue-600 hover:bg-blue-700">
                  Search
                </Button>
                <Button onClick={loadCuratedImages} variant="outline">
                  Curated
                </Button>
                <label className="cursor-pointer">
                  <Button as="span" variant="outline" disabled={uploadingImage}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loadingImages || uploadingImage ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-gray-600">
                    {uploadingImage ? 'Uploading...' : 'Loading images...'}
                  </span>
                </div>
              ) : stockImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {stockImages.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => selectImage(image.url)}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group hover:ring-4 hover:ring-blue-500 transition-all"
                    >
                      <img
                        src={image.thumbnail || image.url}
                        alt={image.alt || 'Stock image'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-white px-4 py-2 rounded-full text-sm font-semibold">
                            Select
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No images found. Try searching or upload your own!</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Stock images from Pexels & Pixabay • Upload your own images
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
